import React, { useEffect, useState } from "react";
import { IInputFilesProps, FileMetadata } from "./definitions";
import styles from "./style.module.scss";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Table from "@amzn/awsui-components-react/polaris/table";
import {
  FileUpload,
  Input,
  Icon,
  Select,
  Button,
  Box,
  Checkbox,
} from "@amzn/awsui-components-react";
import { useGaiaObjectUiContent } from "features/ui-content/hooks";
import { getFileSize, getFileExtension } from "utils/date-format";
import {
  truncateFileName,
  createFileMetadata,
  removeFileFromMetadata,
  updateFileRenamingState,
  updateFileNameInMetadata,
  updateDocumentTypeInMetadata,
  updateGroupInMetadata,
  filterUniqueFiles,
  combineFileMetadata,
} from "utils/input-files-utils";

const InputFiles: React.FC<IInputFilesProps> = (props: IInputFilesProps) => {
  const [fileMetadata, setFileMetadata] = useState<FileMetadata[]>([]);
  const uiContent: any = useGaiaObjectUiContent(props.gaiaObjectType);

  useEffect(() => {
    if (props.files && props.files.length > 0 && !props.fileMetadata) {
      const metadata = createFileMetadata(
        props.files,
        props.defaultDocumentType,
      );
      setFileMetadata(metadata);
      if (props.onFileMetadataChange) {
        props.onFileMetadataChange(metadata);
      }
    } else if (props.fileMetadata) {
      setFileMetadata(props.fileMetadata);
    }
  }, [props.files, props.fileMetadata]);

  const handleFileChange = ({ detail }: any) => {
    const newFiles = detail.value as File[];

    // Prevent file removal during upload
    const isRemoval = newFiles.length < props.files.length;
    if (isRemoval && props.isUploading) {
      return;
    }

    if (props.enableRenaming || props.enableDocumentTypes) {
      if (isRemoval) {
        const newFileNames = newFiles.map((f) => f.name);
        const updatedMetadata = fileMetadata.filter((item) =>
          newFileNames.includes(item.file.name),
        );

        setFileMetadata(updatedMetadata);
        props.setFiles(newFiles);

        if (props.onFileMetadataChange) {
          props.onFileMetadataChange(updatedMetadata);
        }
      } else {
        const uniqueNewFiles = filterUniqueFiles(props.files, newFiles);
        const combinedFiles = [...props.files, ...uniqueNewFiles];
        props.setFiles(combinedFiles);

        const combinedMetadata = combineFileMetadata(
          fileMetadata,
          uniqueNewFiles,
          props.defaultDocumentType,
        );
        setFileMetadata(combinedMetadata);

        if (props.onFileMetadataChange) {
          props.onFileMetadataChange(combinedMetadata);
        }
      }
    } else {
      props.setFiles(newFiles);
    }
  };

  const removeFile = (id: string) => {
    // Prevent file removal during upload
    if (props.isUploading) {
      return;
    }

    const { updatedMetadata, updatedFiles } = removeFileFromMetadata(
      fileMetadata,
      id,
    );

    setFileMetadata(updatedMetadata);
    props.setFiles(updatedFiles);

    if (props.onFileMetadataChange) {
      props.onFileMetadataChange(updatedMetadata);
    }
  };

  const startRename = (id: string) => {
    const updatedMetadata = updateFileRenamingState(fileMetadata, id, true);
    setFileMetadata(updatedMetadata);

    if (props.onFileMetadataChange) {
      props.onFileMetadataChange(updatedMetadata);
    }
  };

  const cancelRename = (id: string) => {
    const updatedMetadata = updateFileRenamingState(fileMetadata, id, false);
    setFileMetadata(updatedMetadata);

    if (props.onFileMetadataChange) {
      props.onFileMetadataChange(updatedMetadata);
    }
  };

  const confirmRename = (id: string) => {
    const updatedMetadata = updateFileRenamingState(fileMetadata, id, false);
    setFileMetadata(updatedMetadata);

    if (props.onFileMetadataChange) {
      props.onFileMetadataChange(updatedMetadata);
    }
  };

  const updateFileName = (id: string, newName: string) => {
    const updatedMetadata = updateFileNameInMetadata(fileMetadata, id, newName);
    setFileMetadata(updatedMetadata);

    if (props.onFileMetadataChange) {
      props.onFileMetadataChange(updatedMetadata);
    }
  };

  const updateDocumentType = (id: string, documentType: string) => {
    const updatedMetadata = updateDocumentTypeInMetadata(
      fileMetadata,
      id,
      documentType,
    );
    setFileMetadata(updatedMetadata);

    if (props.onFileMetadataChange) {
      props.onFileMetadataChange(updatedMetadata);
    }
  };

  const updateGroup = (id: string, group: number) => {
    const updatedMetadata = updateGroupInMetadata(fileMetadata, id, group);
    setFileMetadata(updatedMetadata);

    if (props.onFileMetadataChange) {
      props.onFileMetadataChange(updatedMetadata);
    }
  };

  const getFileName = (item: FileMetadata) => {
    const fileExtension = getFileExtension(item.file.name);
    const truncatedFileName = truncateFileName(item.fileName, 50);
    const fullFileName = `${truncatedFileName}${fileExtension}`;

    if (item.isRenaming && props.enableRenaming) {
      return (
        <div className={styles.renameInline}>
          <div className={styles.renameInput}>
            <Input
              onChange={({ detail }) => updateFileName(item.id, detail.value)}
              value={item.fileName}
            />
          </div>
          <span className={styles.fileExtension}>{fileExtension}</span>
          <span
            onClick={() => cancelRename(item.id)}
            className={styles.renameButton}
          >
            <Icon name="close" />
          </span>
          <span
            onClick={() => confirmRename(item.id)}
            className={styles.renameButton}
          >
            <Icon name="check" />
          </span>
        </div>
      );
    }
    return (
      <div className={styles.fileNameCell}>
        <span
          className={`${styles.fileName} ${
            props.enableRenaming ? styles.clickable : ""
          }`}
          onClick={
            props.enableRenaming ? () => startRename(item.id) : undefined
          }
          title={`${item.fileName}${fileExtension}`}
        >
          {fullFileName}
        </span>
      </div>
    );
  };

  // Build column definitions based on enabled features
  const getColumnDefinitions = () => {
    const hasReplaceColumn = props.showReplaceColumn;
    const baseColumns = [
      {
        id: "fileName",
        header: "File name",
        cell: (item: any) => {
          if (props.enableRenaming || props.enableDocumentTypes) {
            return getFileName(item);
          }
          return item.name || item.file?.name;
        },
        width: props.enableDocumentTypes
          ? "35%"
          : hasReplaceColumn
            ? "70%"
            : "50%",
      },
      {
        id: "size",
        header: "File size",
        cell: (item: any) => {
          if (props.enableRenaming || props.enableDocumentTypes) {
            return getFileSize(item.file.size);
          }
          return getFileSize(item.size);
        },
        width: "15%",
      },
    ];

    const columns = [...baseColumns];

    // Add file type column if enabled
    if (props.enableDocumentTypes) {
      const documentTypeOptions =
        props.documentTypeOptions || uiContent.documentTypeOptions || [];
      columns.splice(1, 0, {
        id: "documentType",
        header: "File type",
        cell: (item: FileMetadata) => (
          <Select
            selectedOption={
              item.documentType
                ? documentTypeOptions.find(
                    (opt: any) => opt.value === item.documentType,
                  ) || null
                : null
            }
            onChange={({ detail }) => {
              updateDocumentType(item.id, detail.selectedOption?.value || "");
            }}
            readOnly={!!props.defaultDocumentType}
            options={documentTypeOptions}
            placeholder="Select type"
            filteringType="auto"
            expandToViewport={true}
          />
        ),
        width: hasReplaceColumn ? "20%" : "25%",
      });
    }

    // Group column requires metadata mode (fileMetadata with id/group).
    if (
      props.enableGroups &&
      (props.enableRenaming || props.enableDocumentTypes)
    ) {
      const maxGroup = Math.max(
        fileMetadata.length,
        ...fileMetadata.map((item) => item.group),
      );
      const groupOptions = Array.from({ length: maxGroup }, (_v, index) => ({
        label: String(index + 1),
        value: String(index + 1),
      }));
      columns.push({
        id: "group",
        header: "Group",
        cell: (item: FileMetadata) => (
          <Select
            selectedOption={{
              label: String(item.group),
              value: String(item.group),
            }}
            onChange={({ detail }) => {
              updateGroup(item.id, Number(detail.selectedOption?.value));
            }}
            options={groupOptions}
            expandToViewport={true}
          />
        ),
        width: "15%",
      });
    }

    // Add replace existing column if enabled
    if (hasReplaceColumn) {
      columns.push({
        id: "replaceExisting",
        header: "Replace existing",
        cell: (item: any) => {
          const fileName = item.file?.name ?? item.name;
          return (
            <Checkbox
              checked={props.replaceExistingMap?.get(fileName) ?? false}
              disabled={!props.duplicateFiles?.has(fileName)}
              onChange={({ detail }) => {
                props.onReplaceExistingChange?.(fileName, detail.checked);
              }}
            />
          );
        },
        width: "15%",
      });
    }

    // Add action column (remove button) if file type or renaming enabled
    if (props.enableRenaming || props.enableDocumentTypes) {
      columns.push({
        id: "action",
        header: "Action",
        cell: (item: FileMetadata) => (
          <div className={styles.removeButton}>
            <Button
              variant="link"
              onClick={() => removeFile(item.id)}
              disabled={props.isUploading}
            >
              Remove
            </Button>
          </div>
        ),
        width: props.enableDocumentTypes ? "15%" : "15%",
      });
    }

    return columns;
  };

  const getTableItems = () => {
    if (props.enableRenaming || props.enableDocumentTypes) {
      return fileMetadata;
    }
    return props.files;
  };

  return (
    <div className={styles.flexColumnFull}>
      <div className={styles.fileUploadSection}>
        <FileUpload
          onChange={handleFileChange}
          value={props.files}
          i18nStrings={{
            uploadButtonText: (e) => (e ? "Browse files" : "Upload"),
            dropzoneText: (e) =>
              e ? "Drop files to upload" : "Drop file to upload",
            removeFileAriaLabel: (e) => `Remove file ${e + 1}`,
            errorIconAriaLabel: "Error",
          }}
          accept={
            props.acceptedFileTypes ||
            ".docm, .pages, .mp4, .xls, .doc, .docx, .xls, .xlsx, .csv, .tsv, .pdf, .jpg, .jpeg, .png, .eml, .ppt, .pptx, .mp4, .zip"
          }
          multiple={props.multiple !== false}
          constraintText={<div>Or drop files here</div>}
          tokenLimit={
            props.enableRenaming || props.enableDocumentTypes ? 0 : undefined
          }
        />
      </div>

      <div className={styles.tableContainer}>
        <Table
          columnDefinitions={getColumnDefinitions()}
          items={getTableItems()}
          empty={
            <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
              <SpaceBetween size="m">
                <b>No files</b>
              </SpaceBetween>
            </Box>
          }
          trackBy={
            props.enableRenaming || props.enableDocumentTypes ? "id" : undefined
          }
        />
      </div>
    </div>
  );
};

export default InputFiles;
